// Pre-filled example content per block type. When a photographer adds a
// new block they can choose "Empty" or one of these examples; the chosen
// content seeds the new block. Photographer-edits-from-here is faster than
// staring at a blank shell.
//
// Keep examples tasteful and photography-flavoured but generic enough to
// fit weddings, portraits, family, brand work. Avoid client names.

import type { BlockContent, BlockType } from "./api";

export interface BlockExample {
  label: string;
  description?: string;
  content: BlockContent;
}

export const BLOCK_EXAMPLES: Record<BlockType, BlockExample[]> = {
  statement: [
    {
      label: "Mission statement",
      content: {
        label: "PHILOSOPHY",
        heading: "Designed for moments that move you.",
        align: "left",
      } as BlockContent,
    },
    {
      label: "Welcome",
      content: {
        label: "HELLO",
        heading: "Stories told quietly, frame by frame.",
        align: "center",
      } as BlockContent,
    },
  ],
  hero: [
    {
      label: "Wedding photographer",
      content: {
        heading: "Wedding photography that feels like you.",
        subtext: "Documentary, unposed, told in light.",
        cta_label: "See available dates",
        cta_url: "/contact",
      } as BlockContent,
    },
    {
      label: "Family portrait",
      content: {
        heading: "Family stories, captured honestly.",
        subtext: "In your home, on your favourite beach, wherever life is.",
        cta_label: "Book a session",
        cta_url: "/contact",
      } as BlockContent,
    },
    {
      label: "Brand session",
      content: {
        heading: "Photographs your brand actually needs.",
        subtext: "Editorial brand work for studios, makers, and small teams.",
        cta_label: "Start a project",
        cta_url: "/contact",
      } as BlockContent,
    },
  ],
  full_bleed: [
    {
      label: "Dramatic cover",
      content: {
        heading: "Light is everything.",
        subtext: "Selected work, 2024–2026.",
        height: "full",
        overlay: 0.45,
        text_align: "center",
        cta_label: "Explore galleries",
        cta_url: "/galleries",
      } as BlockContent,
    },
  ],
  split: [
    {
      label: "About + image",
      content: {
        image_side: "left",
        heading: "Where it started",
        body:
          "I'm a photographer based in the Netherlands. I started with film as a teenager and I haven't stopped chasing light since. My work focuses on real moments — the half-smiles, the held hands, the quiet looks between speeches.",
        cta_label: "My approach →",
        cta_url: "/about",
      } as BlockContent,
    },
  ],
  text: [
    {
      label: "About paragraph",
      content: {
        markdown:
          "I believe the best photographs come from being trusted. I'll show up early, learn names, blend in, and disappear behind the camera. By the end of the day you'll have a friend with a Sony A7IV.\n\nI deliver in 4–6 weeks. Every gallery includes a private link you can share with family, and downloads are always free.",
      } as BlockContent,
    },
  ],
  image: [
    {
      label: "Single image",
      content: {
        caption: "Sunset, 17 minutes after the ceremony.",
        layout: "wide",
      } as BlockContent,
    },
  ],
  quote: [
    {
      label: "Client testimonial",
      content: {
        body:
          "We booked {{photographer name}} for our wedding and felt like we had a friend with a camera by the end of the night. The photos are exactly the day we remember - not posed, not stiff, just us.",
        author: "Lotte & Tim, June 2025",
      } as BlockContent,
    },
  ],
  callout: [
    {
      label: "Booking notice",
      content: {
        icon: "📷",
        heading: "Booking 2026 dates",
        body: "Three weekend dates left for late summer. Get in touch by 1 July.",
      } as BlockContent,
    },
  ],
  faq: [
    {
      label: "Wedding FAQ",
      content: {
        faq_items: [
          {
            ID: "",
            Question: "How far in advance should we book?",
            Answer:
              "Most couples book 6–12 months ahead, but I sometimes have shorter-notice openings — just ask.",
            SortOrder: 0,
          },
          {
            ID: "",
            Question: "Do you travel?",
            Answer: "Yes — anywhere in Europe. Beyond Europe, ask and we'll work it out.",
            SortOrder: 1,
          },
          {
            ID: "",
            Question: "When do we get the photos?",
            Answer:
              "Sneak peek within a week, full gallery within six weeks. You'll get unlimited downloads in a private gallery you can share with family.",
            SortOrder: 2,
          },
        ],
      } as BlockContent,
    },
  ],
  services: [
    {
      label: "Wedding pricing",
      content: {
        services_items: [
          {
            ID: "",
            Name: "Half-day coverage",
            Price: "€ 1,400",
            Description: "5 hours, prep through first dance. ~400 edited images.",
            SortOrder: 0,
          },
          {
            ID: "",
            Name: "Full-day coverage",
            Price: "€ 2,200",
            Description: "9 hours, prep through party. ~700 edited images.",
            SortOrder: 1,
          },
          {
            ID: "",
            Name: "Elopement",
            Price: "€ 950",
            Description: "3 hours, ceremony + portraits. Travel free within NL.",
            SortOrder: 2,
          },
        ],
      } as BlockContent,
    },
  ],
  process: [
    {
      label: "How we work together",
      content: {
        intro:
          "Booking me is light on admin. Three steps, mostly conversation:",
        process_steps: [
          { id: "", heading: "Say hello", body: "Tell me about your day — venue, vibe, who's there.", sort_order: 0 },
          { id: "", heading: "Plan a call", body: "30-minute video chat so we can get to know each other.", sort_order: 1 },
          { id: "", heading: "Lock the date", body: "30% deposit holds the date. Final payment a week before.", sort_order: 2 },
        ],
      } as BlockContent,
    },
  ],
  gallery_row: [
    {
      label: "3-column row",
      content: { columns: 3, items: [] } as BlockContent,
    },
    {
      label: "4-column row",
      content: { columns: 4, items: [] } as BlockContent,
    },
  ],
  divider: [
    { label: "Line divider", content: { style: "line" } as BlockContent },
    { label: "Spacer (no line)", content: { style: "space" } as BlockContent },
  ],
  video: [
    {
      label: "Vimeo embed",
      content: {
        url: "https://vimeo.com/123456789",
        caption: "Behind the scenes — June 2025",
      } as BlockContent,
    },
  ],
  gallery_list: [
    {
      label: "All galleries",
      content: {} as BlockContent,
    },
  ],
  contact_form: [
    {
      label: "Contact form",
      content: {
        heading: "Let's talk",
        body: "Fill in the form below and I'll be in touch within 48 hours.",
      } as BlockContent,
    },
  ],
  section: [
    {
      label: "Row",
      description: "Side-by-side layout",
      content: { direction: "row", gap: "md", align: "stretch", justify: "start", padding: "none" } as unknown as BlockContent,
    },
    {
      label: "Column",
      description: "Stacked layout",
      content: { direction: "column", gap: "md", align: "stretch", justify: "start", padding: "none" } as unknown as BlockContent,
    },
  ],
};
